import { toast } from '@wellesley/system-toast'
import { getLocation, getLocationPermissionState } from 'helpers/geo'
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import {
  AddressData,
  useGetApiV1GeoQuery,
  useGetApiV1AddressesSuggestQuery,
} from '@wellesley/openapi'

type GeoCoords = { latitude: number; longitude: number } | null

const DEBOUNCE_MS = 350
const DELAY = 300
const LOCATION_ACCESS_DENIED_MESSAGE =
  'Location access denied. Enable it for automatic detection.'
const getPermissionState =
  getLocationPermissionState as () => Promise<PermissionState | null>

type UseLocationSelectorProps = {
  location: AddressData | null
  isOpenLocationInput: boolean
  setLocation: (this: void, value: AddressData) => void
  clearLocation: (this: void) => void
  toggleOpenLocationInput: (this: void) => void
}

const getInputValue = (location: AddressData | null) =>
  location?.formatted ?? ''

const useLocationSelector = ({
  location,
  isOpenLocationInput,
  setLocation,
  clearLocation,
  toggleOpenLocationInput,
}: UseLocationSelectorProps) => {
  const [inputValue, setInputValue] = useState(getInputValue(location))
  const [pendingLocation, setPendingLocation] = useState<AddressData | null>(
    location,
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [geoSearchText, setGeoSearchText] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [autoDetect, setAutoDetect] = useState(false)
  const [geoCoords, setGeoCoords] = useState<GeoCoords>(null)

  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const wasOpenRef = useRef(isOpenLocationInput)

  const { data: suggestions = [], isFetching: isLoadingSuggestions } =
    useGetApiV1AddressesSuggestQuery(
      { text: searchTerm },
      { skip: !searchTerm.trim() },
    )

  const { data: geoSuggestions = [] } = useGetApiV1AddressesSuggestQuery(
    { text: geoSearchText },
    { skip: !geoSearchText.trim() },
  )

  const { data: detectedAddress } = useGetApiV1GeoQuery(
    {
      latitude: geoCoords?.latitude ?? 0,
      longitude: geoCoords?.longitude ?? 0,
    },
    { skip: !geoCoords },
  )

  useEffect(() => {
    if (!detectedAddress || !geoCoords) return
    const formatted = detectedAddress.replace(/^"|"$/g, '')
    setInputValue(formatted)
    setGeoSearchText(formatted)
    setIsDropdownOpen(false)
  }, [detectedAddress, geoCoords])

  useEffect(() => {
    if (!geoSearchText || !geoSuggestions.length) return
    setPendingLocation(geoSuggestions[0])
    setInputValue(geoSuggestions[0].formatted)
    setGeoSearchText('')
  }, [geoSuggestions, geoSearchText])

  useEffect(() => {
    const isOpening = isOpenLocationInput && !wasOpenRef.current
    wasOpenRef.current = isOpenLocationInput

    if (isOpening) {
      setInputValue(getInputValue(location))
      setPendingLocation(location)
      setSearchTerm('')
      const t = window.setTimeout(() => inputRef.current?.focus(), DELAY)
      return () => window.clearTimeout(t)
    }

    if (!isOpenLocationInput) {
      setInputValue(getInputValue(location))
      setPendingLocation(location)
      setSearchTerm('')
      setIsDropdownOpen(false)
      setAutoDetect(false)
      setGeoCoords(null)
    }
  }, [isOpenLocationInput, location?.placeId])

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setPendingLocation(null)
    setIsDropdownOpen(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!value.trim()) {
      setSearchTerm('')
      setAutoDetect(false)
      setGeoCoords(null)
      return
    }
    debounceRef.current = setTimeout(() => {
      setSearchTerm(value)
    }, DEBOUNCE_MS)
  }, [])

  const handleInputFocus = useCallback(() => {
    setIsDropdownOpen(true)
  }, [])

  const handleSelectSuggestion = useCallback((suggestion: AddressData) => {
    setInputValue(suggestion.formatted)
    setPendingLocation(suggestion)
    setSearchTerm('')
    setIsDropdownOpen(false)
    inputRef.current?.focus()
  }, [])

  const handleClearInput = useCallback(() => {
    setInputValue('')
    setSearchTerm('')
    clearLocation()
    setPendingLocation(null)
    setIsDropdownOpen(false)
    toggleOpenLocationInput()
  }, [clearLocation, toggleOpenLocationInput])

  const handleAutoDetectToggle = useCallback((checked: boolean) => {
    setAutoDetect(checked)
    if (!checked) {
      setGeoCoords(null)
      return
    }

    void (async () => {
      const permissionState = await getPermissionState()

      if (permissionState === 'denied') {
        setAutoDetect(false)
        setGeoCoords(null)
        toast.error(LOCATION_ACCESS_DENIED_MESSAGE)
        return
      }

      getLocation({
        onSuccess: (pos) => {
          setGeoCoords({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          })
        },
        onError: (error?: GeolocationPositionError) => {
          setAutoDetect(false)
          setGeoCoords(null)
          if (error?.code === 1) {
            toast.error(LOCATION_ACCESS_DENIED_MESSAGE)
          }
        },
      })
    })()
  }, [])

  const handleSave = useCallback(() => {
    if (!pendingLocation) return
    setLocation(pendingLocation)
    toggleOpenLocationInput()
  }, [pendingLocation, setLocation, toggleOpenLocationInput])

  const handleIconCrossClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      e.stopPropagation()
      clearLocation()
      setInputValue('')
      setPendingLocation(null)
      if (isOpenLocationInput) toggleOpenLocationInput()
    },
    [clearLocation, isOpenLocationInput, toggleOpenLocationInput],
  )

  const isAutoDetecting = autoDetect && (!geoCoords || !detectedAddress)
  const hasSuggestions = !!searchTerm.trim() && suggestions.length > 0
  const showDropdown = isDropdownOpen && isOpenLocationInput

  const trimmedInputValue = inputValue.trim()
  const isSaveDisabled: boolean =
    trimmedInputValue !== (pendingLocation?.formatted ?? '').trim() ||
    trimmedInputValue === (location?.formatted ?? '').trim() ||
    (Boolean(pendingLocation) && !trimmedInputValue)

  return {
    inputValue,
    inputRef,
    anchorRef,
    suggestions,
    isLoadingSuggestions,
    isAutoDetecting,
    autoDetect,
    hasSuggestions,
    showDropdown,
    isSaveDisabled,
    handleInputChange,
    handleInputFocus,
    handleSelectSuggestion,
    handleClearInput,
    handleAutoDetectToggle,
    handleSave,
    handleIconCrossClick,
    setIsDropdownOpen,
  }
}

export default useLocationSelector
